import { AfterViewInit, Component, inject, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { EContentLocation, EContentViewType, EContentWidgetType, EError } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { Content } from './content.model';
import { IContentService, IContentServiceToken } from 'projects/shared/lib/services/data/content/content-service.interface';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { WidgetContentCardBaseComponent } from '../widget/content-card/widget-content-card-base.component';
import { WidgetInfoBaseComponent } from '../widget/info/widget-info-base.component';
import { WidgetProductCardBaseComponent } from '../widget/productcard/widget-product-card-base.component';
import { WidgetTextBaseComponent } from '../widget/text/widget-text-base.component';
import { WidgetWebTextBaseComponent } from '../widget/web-text/widget-web-text-base.component';
import { WidgetHeadlineBaseComponent } from '../widget/headline/widget-headline-base.component';
import { WidgetEmptyBaseComponent } from '../widget/empty/widget-empty-base.component';
import { WidgetTechGroupCardBaseComponent } from '../widget/tech-group-card/widget-tech-group-card-base.component';
import { WidgetNavigationCardBaseComponent } from '../widget/navigation-card/widget-navigation-card-base.component';
import { WidgetImageBaseComponent } from '../widget/image/widget-image-base.component';
import { WidgetBreadcrumbBaseComponent } from '../widget/breadcrumb/widget-breadcrumb-base.component';
import { WidgetFullsizeBannerComponent } from "../widget/fullsize-banner/widget-fullsize-banner.component";
import { ILoggerService, ILoggerServiceToken } from 'projects/shared/lib/services/infrastructure/logger/logger-service.interface';
import { Carousel } from 'bootstrap';

@Component({
  selector: 'app-content-base',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    WidgetInfoBaseComponent,
    WidgetProductCardBaseComponent,
    WidgetTextBaseComponent,
    WidgetWebTextBaseComponent,
    WidgetHeadlineBaseComponent,
    WidgetContentCardBaseComponent,
    WidgetEmptyBaseComponent,
    WidgetTechGroupCardBaseComponent,
    WidgetNavigationCardBaseComponent,
    WidgetImageBaseComponent,
    WidgetBreadcrumbBaseComponent,
    WidgetFullsizeBannerComponent
  ],
  templateUrl: './content-base.component.html',
  styleUrls: ['./content-base.component.scss']
})
export class ContentBaseComponent implements OnInit, AfterViewInit, OnChanges {

  @Input() public parent: string | undefined;
  @Input() public location: EContentLocation | undefined;

  @Input() public infoWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public productCardWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public textWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public webTextWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public headlineWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public contentCardWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public emptyWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public techgroupCardWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public navigationCardWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public imageWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public breadcrumbWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public categoriesWidgetTemplate?: TemplateRef<unknown> | undefined;
  @Input() public fullsizeBannerWidgetTemplate?: TemplateRef<unknown> | undefined;

  public eContentViewType: typeof EContentViewType = EContentViewType;
  public eContentWidgetType: typeof EContentWidgetType = EContentWidgetType;

  private hostClassBase: string = `content`;
  public widgetColumnClassBase: string = `widget`;

  public contentList: Content[] | undefined;

  private isLoading: boolean | undefined;
  private readonly logger: ILoggerService;
  private readonly contentService: IContentService;
  public currentCarouselItem: number | undefined = 0;

  public constructor() {
    this.logger = inject(ILoggerServiceToken);
    this.contentService = inject(IContentServiceToken);
  }

  public ngOnInit(): void {
    if (this.parent == null) {
      return;
    }
    if (this.location == null) {
      return;
    }

    this.contentLoad(this.parent, this.location);
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    for (let propName in changes) {
      switch (propName) {
        case 'parent':
          break;
        case 'location':
          break;
        default: {
          // throw new Error('Input property handler not implemented.'); // removed due to various new template parameters
        }
      }
      if (this.parent == null) {
        return;
      }
      if (this.location == null) {
        return;
      }

      this.contentLoad(this.parent, this.location);
    }
  }

  private contentLoad(parent: string, location: EContentLocation) {
    let parametersEqual = this.parent == parent && this.location == location;
    if (this.isLoading && parametersEqual) {
      return;
    }
    this.isLoading = true;

    try {
      firstValueFrom(this.contentService.contentGet(parent, location))
        .then(
          (result) => {
            this.contentList = result.contentList?.map(
              (c) => {
                let content = new Content(c);
                this.conentViewTypeHandle(content);
                this.attributeListDynamicEntriesHandle(content);
                return content;
              }
            ).sort((a, b) => a.sequence - b.sequence);
            this.isLoading = false;
          }
        ).catch(
          (error) => {
            this.logger.error(error, this.constructor.name);
            this.isLoading = false;
          }
        );
    } catch (error) {
      this.logger.error(error, this.constructor.name);
      this.isLoading = false;
    }
  }

  private conentViewTypeHandle(content: Content) {
    let hostClassDynamic: string = "";

    switch (content.viewType) {
      case EContentViewType.Default:
        hostClassDynamic = 'viewDefault';
        break;
      case EContentViewType.Gallery:
        hostClassDynamic = 'viewGallery row mt-0 g-3';
        break;
      case EContentViewType.Carousel:
        hostClassDynamic = 'viewCarousel';
        break;
      case undefined:
        break;

      default:
        throw new BasicError(`Missing implementation for viewTpe: '${content.viewType}'`, ErrorCodeService.formatErrorCode(EError.F330010));
    }

    content.hostClass = `${this.hostClassBase} ${hostClassDynamic}`;
  }

  private attributeListDynamicEntriesHandle(content: Content): void {
    this.attributeIdHandle(content);

    switch (content.viewType) {
      case EContentViewType.Default:
        this.loadDefaultAttributes(content);
        break;

      case EContentViewType.Gallery:
        this.loadGalleryAttributes(content);
        break;

      case EContentViewType.Carousel:
        this.loadCarouselAttributes(content);
        break;

      default:
        break;
    }
  }


  private loadDefaultAttributes(content: Content) {
  }

  private loadGalleryAttributes(content: Content) {
    this.attributeColumnCountHandle(content);
  }

  private loadCarouselAttributes(content: Content) {
    this.attributeIdHandle(content);
  }

  private attributeColumnCountHandle(content: Content) {
    let contentAttributeList = content.contentAttributeList;
    let columnCountString = contentAttributeList?.find(a => a.key?.localeCompare("ColumnCount") == 0)?.value;
    let columnCount: number | undefined = columnCountString ? Number.parseInt(columnCountString) : undefined;
    let widgetColumnClassDynamic: string = "";

    if (columnCount != undefined) {
      switch (columnCount) {
        case 2:
          widgetColumnClassDynamic = "col-6";
          break;
        case 3:
          widgetColumnClassDynamic = "col-4";
          break;
        case 4:
          widgetColumnClassDynamic = "col-6 col-md-3";
          break;

        default:
          break;
      }
    }

    content.widgetColumnClass = `${this.widgetColumnClassBase} ${widgetColumnClassDynamic}`;
  }

  private attributeIdHandle(content: Content) {
    let contentAttributeList = content.contentAttributeList;

    let idString = contentAttributeList?.find(a => a.key?.localeCompare("Id") === 0)?.value;
    content.id = idString;

    let classString = contentAttributeList?.find(a => a.key?.localeCompare("Class") === 0)?.value;
    content.class = classString;
  }

  public carouselPrev(content: Content) {
    let currentIndex = this.currentCarouselItem;
    if (currentIndex == undefined) {
      this.currentCarouselItem = 0;
      return;
    }
    currentIndex = currentIndex - 1;

    if (currentIndex < 0) {
      currentIndex = content.contentWidgetList?.length;

      if (currentIndex == undefined) {
        currentIndex = 0;
        return;
      }
    }
  }

  public carouselNext(content: Content) {
    let currentIndex = this.currentCarouselItem;
    if (currentIndex == undefined) {
      this.currentCarouselItem = 0;
      return;
    }
    currentIndex = currentIndex + 1;

    if (currentIndex < 0) {
      currentIndex = content.contentWidgetList?.length;

      if (currentIndex == undefined) {
        currentIndex = 0;
        return;
      }
    }
  }

  ngAfterViewInit(): void {
    const OBSERVER = new MutationObserver(() => {
      const BUTTON = document.querySelector(".gallery-mobile-collapse-show");
      const COLLAPSE = document.querySelector(".gallery-mobile-collapse");
      if (BUTTON && COLLAPSE) {
        BUTTON.addEventListener("click", () => {
          COLLAPSE.classList.add("visible");
          BUTTON.remove();
        });
        OBSERVER.disconnect();
      }
    });

    OBSERVER.observe(document.body, {
      childList: true,
      subtree: true
    });

    setTimeout(() => {
      const CAROUSELS = document.querySelectorAll<HTMLDivElement>('.carousel.fullsize-carousel');
      CAROUSELS.forEach((el) => {
        const IS_MULTI = el.classList.contains('multi-slide') || el.id === 'multi-slide';
        if (!IS_MULTI) {
          new Carousel(el, {
            interval: 10000,
            ride: 'carousel',
            pause: false
          });
        }
      });
    });
  }
}